/**
 * projectStatus.js - what counts as a PROJECT rather than a bid.
 *
 * GlobalOps stores one row per quote, so an opportunity and a
 * project are the SAME record; only the status separates them. The Projects
 * tab is the work we actually have (won), and the Opportunities tab is the
 * bid pipeline (open / lost / cancelled / no quote). Anything counting or
 * listing "projects" must use this one definition so the sidebar badge, the
 * page header and the table can never disagree - they did before, and a
 * budgetary quote showed up in the project count (Paul, acceptance test #49).
 *
 * NOT for money totals. Yearly Open/Won/Lost figures count the raw status
 * (status = 'Won'), so Reporting and DashboardView deliberately filter on
 * the raw status instead. GlobalOps is the source of truth since 10/1/26;
 * see docs/ARCHITECTURE.md section 4.
 */

/** Statuses meaning "we won this and it is real work". 'Complete' is a won
 *  job that has finished - it is selectable on the Projects form, so it must
 *  stay in the list or the row would silently vanish from the tab. */
export const PROJECT_STATUSES = ['Won', 'Complete'];

/** True when an opportunity row should appear on the Projects tab. */
export const isProjectRow = (p) => PROJECT_STATUSES.includes((p && p.status) || '');

/** The Closed? flag on a won job (projects.closed: 'YES' / 'Yes' / NULL / 'NO'). */
export const isClosedProject = (p) =>
  String((p && p.closed) || '')
    .trim()
    .toUpperCase() === 'YES';

/** A won job still running - the sidebar Projects badge, the Dashboard's
 *  Active Projects card and the Projects tab's default Active chip. */
export const isActiveProject = (p) => isProjectRow(p) && !isClosedProject(p);

/* Q-01 (10/1/26): an opportunity that was cancelled or not awarded is
 * 'Cancelled'. It was 'Dead' from 9/29 to 10/1/26 (and 'Cancelled' before);
 * the server renames stored rows, and the old spellings still read as
 * Cancelled so a count never drops a row. Lead / site Status 'Dead' (shut
 * down) is a different field and never goes through this. */
const CANCELLED_SPELLINGS = new Set(['cancelled', 'canceled', 'cancel', 'dead']);
export const isCancelledStatus = (status) =>
  CANCELLED_SPELLINGS.has(
    String(status || '')
      .trim()
      .toLowerCase()
  );
